import type { AppraisalHandoff } from '../useAppraisalFlow'
import { buildVehicleHandoffHref, vehicleDetailRows } from '../vehicleHandoff'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import LoanAmountCard from '@/blocks/CarPawnHero/LoanAmountCard'
import { Button } from '@/components/Button'
import { EAppraisalHandoffTarget } from '@/types/gql/graphql'

/** Median requested amounts of the vehicle forms since 2025 (cars 5.000 €, motorcycles 2.000 €). */
const DEFAULT_CAR_AMOUNT = 5000
const DEFAULT_MOTORCYCLE_AMOUNT = 2000
/** Product Owner: the motorcycle slider stops at 75.000 € (cars: 200.000 €). */
const MAX_MOTORCYCLE_AMOUNT = 75_000

export interface HandoffViewProps {
  target: EAppraisalHandoffTarget
  message: string
  vehicle?: AppraisalHandoff['vehicle']
  onReset: () => void
}

export default function HandoffView({
  target,
  message,
  vehicle,
  onReset,
}: HandoffViewProps) {
  const { t } = useTranslation('wertCheck')
  const isVehicle =
    target === EAppraisalHandoffTarget.Car ||
    target === EAppraisalHandoffTarget.Motorcycle

  if (isVehicle) {
    const rows = vehicleDetailRows(vehicle ?? null, t)
    return (
      <div className="bg-static-white rounded-xl shadow-[0_0_2rem_rgba(0,0,0,0.1)] p-6 flex flex-col items-center gap-4 text-center">
        <p className="text-l text-primary">{message}</p>

        {rows.length > 0 && (
          <dl
            className="w-full max-w-80 text-left grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm"
            data-testid="vehicle-handoff-details"
          >
            <dt className="col-span-2 font-semibold text-primary">
              {t('vehicle_detected')}
            </dt>
            {rows.map((row) => (
              <>
                <dt key={`${row.label}-label`} className="text-neutral-600">
                  {row.label}
                </dt>
                <dd key={`${row.label}-value`} className="text-primary">
                  {row.value}
                </dd>
              </>
            ))}
          </dl>
        )}

        <LoanAmountCard
          className="shadow-none border border-neutral-200 w-full"
          ctaText={t('vehicle_cta')}
          initialAmount={
            vehicle?.desiredAmount ??
            (target === EAppraisalHandoffTarget.Motorcycle
              ? DEFAULT_MOTORCYCLE_AMOUNT
              : DEFAULT_CAR_AMOUNT)
          }
          maxAmount={
            target === EAppraisalHandoffTarget.Motorcycle
              ? MAX_MOTORCYCLE_AMOUNT
              : undefined
          }
          buildHref={(loanAmount) =>
            buildVehicleHandoffHref(target, vehicle ?? null, loanAmount)
          }
        />

        <Button type="button" variant="link" onClick={onReset}>
          {t('reset')}
        </Button>
      </div>
    )
  }

  return (
    <div className="bg-static-white rounded-xl shadow-[0_0_2rem_rgba(0,0,0,0.1)] p-6 flex flex-col items-center gap-4 text-center">
      <p className="text-l text-primary">{message}</p>

      <Link href="/kontakt" className="w-full">
        <Button type="button" variant="cta" className="w-full">
          {t('handoff_cta')}
        </Button>
      </Link>

      <Button type="button" variant="link" onClick={onReset}>
        {t('reset')}
      </Button>
    </div>
  )
}
